import React from "react";
import { Link } from "react-router-dom";
import { Icon, Popup } from "semantic-ui-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import DataTable from "../dataTable";
import MemberChip from "../MemberChip";
import styles from "./reportDashboard.module.scss";

const columnClass = {
    figure: styles.figure,
    table: styles.table,
    graph: styles.graph,
};

function FigureValue({ value }) {
    if (value && typeof value === "object" && value.url) {
        return <Link to={value.url}>{value.text || value.url}</Link>;
    }

    if (value && typeof value === "object") {
        return value.text || "";
    }

    return value;
}

function FigureReport({ report }) {
    const values = Array.isArray(report.data) ? report.data : [];
    return (
        <div className={styles.figures}>
            {values.map((item) => (
                <div className={styles.figureValue} key={item.label}>
                    <span>{item.label}</span>
                    <strong>
                        <FigureValue value={item.value} />
                    </strong>
                </div>
            ))}
        </div>
    );
}

function TableReport({ report }) {
    const columns = report.columns || [];
    const rows = Array.isArray(report.data) ? report.data : [];
    const headers = columns.map((column) => ({
        key: column.key,
        text: column.label || column.text || column.key,
    }));
    const data = rows.map((row) => {
        return columns.reduce((carry, column) => {
            const value = row[column.key];
            if (value?.type === "member" && value.member) {
                carry[column.key] = {
                    value: (
                        <MemberChip
                            member={value.member}
                            hideMembership={true}
                        />
                    ),
                    search: value.member.name,
                };
                return carry;
            }

            carry[column.key] = { value, search: value };
            return carry;
        }, {});
    });

    return (
        <div className={styles.tableWrap}>
            <DataTable
                headers={headers}
                data={data}
                hideSearch
                hideResultsCount
                hideExport
            />
        </div>
    );
}

function GraphReport({ report }) {
    const data = Array.isArray(report.data) ? report.data : [];
    const columns = report.columns || [];
    const xColumn = columns[0]?.key || "label";
    const series = columns.slice(1);
    const Chart = report.graphType === "bar" ? BarChart : LineChart;

    return (
        <div className={styles.chart}>
            <ResponsiveContainer width='100%' height={280}>
                <Chart data={data}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey={xColumn} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {series.map((column, index) => {
                        const colour = column.colour || column.color || "#00a3a3";
                        if (report.graphType === "bar") {
                            return (
                                <Bar
                                    key={column.key}
                                    dataKey={column.key}
                                    name={column.label || column.key}
                                    fill={colour}
                                />
                            );
                        }

                        return (
                            <Line
                                key={column.key}
                                dataKey={column.key}
                                name={column.label || column.key}
                                stroke={colour}
                                type='monotone'
                                strokeWidth={index === 0 ? 3 : 2}
                            />
                        );
                    })}
                </Chart>
            </ResponsiveContainer>
        </div>
    );
}

function bodyFor(report) {
    if (report.type === "figure") {
        return <FigureReport report={report} />;
    }

    if (report.type === "graph") {
        return <GraphReport report={report} />;
    }

    return <TableReport report={report} />;
}

function InfoContent({ report }) {
    return (
        <div>
            {report.description && <p>{report.description}</p>}
            {report.info && <p>{report.info}</p>}
        </div>
    );
}

function ReportCard({ report }) {
    const hasInfoContent = report.description || report.info;

    return (
        <div className={`${styles.card} ${columnClass[report.type] || styles.figure}`}>
            <div className={styles.header}>
                <div>
                    <h4>{report.name}</h4>
                    {report.usesDateFilter === false && (
                        <span className={styles.filterNote}>
                            Not affected by period filters
                        </span>
                    )}
                </div>
                {hasInfoContent && (
                    <Popup
                        content={<InfoContent report={report} />}
                        trigger={<Icon name='info circle' />}
                    />
                )}
            </div>
            {bodyFor(report)}
        </div>
    );
}

export default ReportCard;
